import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  formatDate,
  formatNumber,
  getAbbrValue,
  getRandomColor,
} from '../../../core/helpers';
import { palleteMap } from '../SellerDashboard';
import './SellerSalesChart.scss';

interface Props {
  sales: SalseItem[];
}

interface SalseItem {
  country: string;
  amount: number;
  date: Date;
}
interface ChartItem {
  All: number;
  month: string;
  [key: string]: string | number;
}

export default function SellerSalesChart({ sales }: Props) {
  const { t, i18n } = useTranslation();

  const keys = useMemo(
    () => [
      'All',
      ...sales
        .map((a) => a.country)
        .filter((a, i, self) => self.indexOf(a) === i),
    ],
    [sales]
  );

  const chartData: ChartItem[] = useMemo(() => {
    return Object.entries(
      sales.reduce(
        (
          acc: {
            [key: string]: {
              [key: string]: number;
            };
          },
          i
        ) => {
          const v = acc[i.date.toISOString()] || {};
          acc[i.date.toISOString()] = {
            ...v,
            [i.country]: (v?.[i.country] || 0) + i.amount,
          };

          return acc;
        },
        {}
      )
    ).map(([k, v]) => {
      const item: ChartItem = {
        month: k,
        All: Object.values(v).reduce((acc, i) => {
          return (acc += i);
        }, 0),
        ...v,
      };

      const missingKeys = keys.filter(
        (key) => !Object.keys(item).includes(key)
      );

      if (!!missingKeys.length) {
        missingKeys.forEach((key) => {
          item[key] = 0;
        });
      }

      return item;
    });
  }, [sales, keys]);

  const pallete = useMemo(
    () =>
      keys.reduce(
        (
          acc: {
            [key: string]: string;
          },
          k
        ) => {
          acc[k] = palleteMap[k] || getRandomColor();
          return acc;
        },
        {}
      ),
    [keys]
  );

  return (
    <div>
      <div className='row gx-3 row-cols-auto mb-3'>
        <div className='col fw-600'>Marketplaces</div>
        {keys.map((item, index) => {
          return (
            <div className='col' key={item}>
              <div className='d-flex align-items-center user-select-none'>
                <div
                  className='SellerDashboard__circle rounded-circle me-1 flex-shrink-0 me-1'
                  style={{
                    backgroundColor: pallete[item],
                  }}
                />
                {t(item)}
              </div>
            </div>
          );
        })}
      </div>
      <div
        className='d-flex flex-column'
        style={{
          height: 300,
        }}
      >
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            width={500}
            height={300}
            data={chartData}
            margin={{
              right: 30,
              left: i18n.language === 'de' ? 10 : -8,
              bottom: 5,
            }}
          >
            <CartesianGrid stroke='#F1F3F4' />
            <XAxis
              dataKey='month'
              tickLine={false}
              tick={{
                fill: '#264F3F',
                fontSize: 13,
              }}
              stroke='#E5E5E5'
              tickMargin={8}
              tickFormatter={(v) => formatDate(new Date(v), 'MMM')}
            />
            <YAxis
              tickMargin={8}
              tick={{
                fill: '#929F9A',
                fontSize: 13,
              }}
              tickLine={false}
              tickFormatter={(v) => '€' + getAbbrValue(+v / 100)}
              axisLine={false}
            />
            <Tooltip
              allowEscapeViewBox={{
                x: true,
                y: true,
              }}
              cursor={false}
              content={(props) => {
                if (!props.payload?.length) return null;
                const payload: ChartItem = props.payload[0].payload;

                return (
                  <div className='SellerSalesChart__tooltip position-relative'>
                    <div className='fw-600 mb-2'>
                      {formatDate(new Date(payload.month), 'MMMM yyyy')}
                    </div>
                    {Object.entries(payload).map(([k, v]) => {
                      if (k === 'month' || k === 'All') return null;
                      return (
                        <div className='mb-2' key={k}>
                          {t(k)}:&nbsp;
                          {v
                            ? formatNumber(+v / 100, {
                                currency: 'eur',
                              })
                            : 0}
                        </div>
                      );
                    })}
                    <div className='border-top pt-2'>
                      {t('All')}:&nbsp;
                      {formatNumber(payload.All / 100, {
                        currency: 'eur',
                      })}
                    </div>
                  </div>
                );
              }}
            />

            {keys.map((k, index) => (
              <Line
                type='linear'
                dataKey={k}
                stroke={pallete[k]}
                activeDot={{ r: 4 }}
                dot={false}
                key={index}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
